/**
 * Secret API service.
 * Provides operations for managing user secrets.
 *
 * API Endpoints:
 * - GET /secrets - List all secrets
 * - PUT /secrets/:id - Upsert a secret
 * - DELETE /secrets/:id - Delete a secret
 *
 * @module services/secretService
 */

import { httpClient } from './httpClient';
import type {
  SecretResponse,
  SecretListResponse,
  SecretKeysResponse,
  UpsertSecretRequest,
} from '@/types';

const ENDPOINT = '/secrets';

/**
 * Secret Service
 * Encapsulates all secret API operations
 */
export const secretService = {
  /**
   * List all secrets (metadata only, values are not returned)
   * @returns Promise<SecretResponse[]> - Array of secret metadata
   */
  async list(): Promise<SecretResponse[]> {
    console.log('[SecretService] Fetching secrets...');
    try {
      const response = await httpClient.get<SecretListResponse>(ENDPOINT);
      console.log('[SecretService] Fetched', response.data.secrets.length, 'secrets');
      return response.data.secrets;
    } catch (error) {
      console.error('[SecretService] Failed to fetch secrets:', error);
      throw error;
    }
  },

  /**
   * Upsert a secret (create or replace all key-value pairs)
   * @param secretId - The secret ID
   * @param values - Key-value pairs to set
   * @returns Promise<SecretResponse> - The secret metadata
   */
  async upsert(secretId: string, values: Record<string, string>): Promise<SecretResponse> {
    console.log('[SecretService] Upserting secret:', secretId);
    const encodedId = encodeURIComponent(secretId);
    const request: UpsertSecretRequest = { values };
    try {
      const response = await httpClient.put<SecretResponse>(`${ENDPOINT}/${encodedId}`, request);
      console.log('[SecretService] Upserted secret:', response.data.id);
      return response.data;
    } catch (error) {
      console.error('[SecretService] Failed to upsert secret:', secretId, error);
      throw error;
    }
  },

  /**
   * Delete a secret
   * @param secretId - The secret ID
   */
  async delete(secretId: string): Promise<void> {
    console.log('[SecretService] Deleting secret:', secretId);
    const encodedId = encodeURIComponent(secretId);
    try {
      await httpClient.delete(`${ENDPOINT}/${encodedId}`);
      console.log('[SecretService] Deleted secret:', secretId);
    } catch (error) {
      console.error('[SecretService] Failed to delete secret:', secretId, error);
      throw error;
    }
  },

  /**
   * List key names for a secret (values are never transferred).
   * Uses GET /secrets/{id}/keys.
   *
   * @param secretId - The secret ID
   * @returns Promise<string[]> - Array of key names
   */
  async listKeys(secretId: string): Promise<string[]> {
    const encodedId = encodeURIComponent(secretId);
    try {
      const response = await httpClient.get<SecretKeysResponse>(`${ENDPOINT}/${encodedId}/keys`);
      return response.data.keys;
    } catch (error) {
      console.error('[SecretService] Failed to fetch keys for secret:', error);
      throw error;
    }
  },
};
